import { lazy, Suspense, useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'

import { Toaster } from '@/components/ui/Toaster'
import { router } from '@/router'
import { useAuthStore } from '@/stores/auth'
import { watchSystemTheme } from '@/stores/ui'

const PlayerApp = lazy(() => import('@/player/PlayerApp'))

/**
 * The players' own site: `/me` on the console's host, everything on a host of
 * its own (app.unityrp.it). Decided once, on load: the two never share a page.
 */
function isPlayerSite(): boolean {
  const { hostname, pathname } = window.location
  return hostname.startsWith('app.') || pathname === '/me' || pathname.startsWith('/me/')
}

/**
 * Where a `/me` page on the console's host belongs once the players' site has a
 * host of its own (`PLAYER_URL`, baked in at build): links from before the move,
 * bookmarks and all. Null on that host itself, or with no host set.
 */
function playerSiteElsewhere(): string | null {
  const configured = import.meta.env.VITE_PLAYER_URL
  if (!configured) return null
  let origin: string
  try {
    origin = new URL(configured).origin
  } catch {
    return null
  }
  const { origin: here, pathname, search, hash } = window.location
  if (origin === here) return null
  return `${origin}${pathname}${search}${hash}`
}

function ConsoleApp() {
  const bootstrap = useAuthStore((state) => state.bootstrap)

  useEffect(() => {
    void bootstrap()
    return watchSystemTheme()
  }, [bootstrap])

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  )
}

export default function App() {
  if (!isPlayerSite()) return <ConsoleApp />
  const elsewhere = playerSiteElsewhere()
  if (elsewhere) {
    window.location.replace(elsewhere)
    return <div className="bg-background min-h-dvh" />
  }
  return (
    <Suspense fallback={<div className="bg-background min-h-dvh" />}>
      <PlayerApp />
    </Suspense>
  )
}
